import { useState, useMemo } from "react";
import { computeFamilySize, priceIndividualMedical } from "@fa/pricing";
import { individualMedicalRates } from "@fa/rates";
import { individualMedicalPayloadSchema, todayIso } from "@fa/shared";
import {
  Field,
  FormError,
  IdentitySection,
  QuoteTable,
  RatesDisclaimer,
  ReviewSummary,
  StepIndicator,
  StepNavigation,
  SubmitButton,
} from "../components/Shared";
import { useFormValidation } from "../hooks/useFormValidation";
import { useStepWizard, type StepDef } from "../hooks/useStepWizard";
import { submit, useApiBase } from "../api";

function limitOptions(limits: number[], optional = false) {
  return [
    ...(optional
      ? [<option key={0} value={0}>Not included</option>]
      : []),
    ...limits.map((limit) => (
      <option key={limit} value={limit}>
        KES {limit.toLocaleString()}
      </option>
    )),
  ];
}

export function IndividualMedicalForm() {
  const api = useApiBase();
  const [identity, setIdentity] = useState({
    icpakMemberNumber: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [form, setForm] = useState({
    ageOfPrincipal: 40,
    includeSpouse: false,
    numberOfChildren: 0,
    inpatientLimitKes: 500000,
    outpatientLimitKes: 0,
    maternityLimitKes: 0,
    dentalLimitKes: 0,
    opticalLimitKes: 0,
    coverStartDate: todayIso(),
  });
  const [reference, setReference] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const payload = { ...identity, ...form };
  const { errors, allErrors, isValid, parsed, touch, touchAll } = useFormValidation(
    individualMedicalPayloadSchema,
    payload,
  );
  const quote = isValid && parsed
    ? priceIndividualMedical(parsed, individualMedicalRates)
    : null;

  const steps: StepDef[] = useMemo(
    () => [
      {
        key: "identity",
        label: "Member Details",
        validate: () => {
          const fields = ["icpakMemberNumber", "firstName", "lastName", "phone", "email"];
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) { fields.forEach(touch); return false; }
          return true;
        },
      },
      {
        key: "family",
        label: "Family Information",
        validate: () => {
          const fields = ["ageOfPrincipal", "numberOfChildren", "coverStartDate"];
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) { fields.forEach(touch); return false; }
          return true;
        },
      },
      {
        key: "limits",
        label: "Coverage Limits",
        validate: () => {
          const fields = ["inpatientLimitKes", "outpatientLimitKes", "maternityLimitKes", "dentalLimitKes", "opticalLimitKes"];
          const hasErrors = fields.some((f) => f in allErrors);
          if (hasErrors) { fields.forEach(touch); return false; }
          return true;
        },
      },
      {
        key: "review",
        label: "Review & Submit",
      },
    ],
    [allErrors, touch],
  );

  const wizard = useStepWizard(steps);

  async function onSubmit() {
    if (!isValid) { touchAll(); return; }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submit(api, "individual_medical", parsed!);
      setReference(res.reference);
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  // After submission: confirmation
  if (reference) {
    return (
      <div className="fa-bg-green-50 fa-border fa-border-green-300 fa-rounded-md fa-p-4 fa-text-center fa-mt-4">
        <p className="fa-text-green-800 fa-font-semibold">Quote Request Submitted</p>
        <p className="fa-text-green-700 fa-text-sm fa-mt-1">
          Your reference is <b>{reference}</b>.
        </p>
        <p className="fa-text-green-700 fa-text-sm fa-mt-1">
          Our team will contact you at <b>{identity.email}</b> within one (1) working day
          with a detailed quote and payment instructions.
        </p>
      </div>
    );
  }

  return (
    <div ref={wizard.containerRef}>
      <StepIndicator
        steps={steps}
        currentIndex={wizard.currentStepIndex}
        completedSteps={wizard.completedSteps}
        onStepClick={wizard.goTo}
      />

      {wizard.currentStep.key === "identity" && (
        <div className="fa-step-enter" key="identity">
          <IdentitySection value={identity} onChange={setIdentity} errors={errors} onBlur={touch} />
        </div>
      )}

      {wizard.currentStep.key === "family" && (
        <div className="fa-step-enter" key="family">
          <div className="fa-section fa-section-orange fa-space-y-3">
            <Field id="ageOfPrincipal" label="Age of Principal" error={errors.ageOfPrincipal} required>
              <input
                id="ageOfPrincipal"
                type="number"
                value={form.ageOfPrincipal}
                onChange={(e) =>
                  setForm({ ...form, ageOfPrincipal: Number(e.target.value) })
                }
                onBlur={() => touch("ageOfPrincipal")}
              />
            </Field>
            <Field id="includeSpouse" label="Include Spouse?">
              <select
                id="includeSpouse"
                value={form.includeSpouse ? "YES" : "NO"}
                onChange={(e) =>
                  setForm({ ...form, includeSpouse: e.target.value === "YES" })
                }
              >
                <option value="NO">NO</option>
                <option value="YES">YES</option>
              </select>
            </Field>
            <Field id="numberOfChildren" label="Number of Children (over 18 must be in school)" error={errors.numberOfChildren}>
              <input
                id="numberOfChildren"
                type="number"
                min={0}
                max={10}
                value={form.numberOfChildren}
                onChange={(e) =>
                  setForm({ ...form, numberOfChildren: Number(e.target.value) })
                }
                onBlur={() => touch("numberOfChildren")}
              />
            </Field>
            <Field id="coverStartDate" label="Cover Start Date" error={errors.coverStartDate} required>
              <input
                id="coverStartDate"
                type="date"
                value={form.coverStartDate}
                min={todayIso()}
                onChange={(e) => setForm({ ...form, coverStartDate: e.target.value })}
                onBlur={() => touch("coverStartDate")}
              />
            </Field>
          </div>
        </div>
      )}

      {wizard.currentStep.key === "limits" && (
        <div className="fa-step-enter" key="limits">
          <div className="fa-font-semibold fa-mb-3">
            Family Size:{" "}
            {computeFamilySize({ includeSpouse: form.includeSpouse, numberOfChildren: form.numberOfChildren })}
          </div>
          <div className="fa-section fa-section-orange fa-space-y-3">
            <Field id="inpatientLimitKes" label="Inpatient Limit Per Family" required>
              <select
                id="inpatientLimitKes"
                value={form.inpatientLimitKes}
                onChange={(e) =>
                  setForm({ ...form, inpatientLimitKes: Number(e.target.value) })
                }
              >
                {limitOptions(individualMedicalRates.inpatient.limits)}
              </select>
            </Field>
            <div className="fa-grid fa-grid-cols-2 fa-md:grid-cols-4 fa-gap-3">
              <Field id="outpatientLimitKes" label="Outpatient Limit Per Person">
                <select
                  id="outpatientLimitKes"
                  value={form.outpatientLimitKes}
                  onChange={(e) =>
                    setForm({ ...form, outpatientLimitKes: Number(e.target.value) })
                  }
                >
                  {limitOptions(individualMedicalRates.outpatient.limits, true)}
                </select>
              </Field>
              <Field id="maternityLimitKes" label="Maternity Limit per Family">
                <select
                  id="maternityLimitKes"
                  value={form.maternityLimitKes}
                  onChange={(e) =>
                    setForm({ ...form, maternityLimitKes: Number(e.target.value) })
                  }
                >
                  {limitOptions(individualMedicalRates.maternity.limits, true)}
                </select>
              </Field>
              <Field id="dentalLimitKes" label="Dental Limit Per Person">
                <select
                  id="dentalLimitKes"
                  value={form.dentalLimitKes}
                  onChange={(e) =>
                    setForm({ ...form, dentalLimitKes: Number(e.target.value) })
                  }
                >
                  {limitOptions(individualMedicalRates.dental.limits, true)}
                </select>
              </Field>
              <Field id="opticalLimitKes" label="Optical Limit Per Person">
                <select
                  id="opticalLimitKes"
                  value={form.opticalLimitKes}
                  onChange={(e) =>
                    setForm({ ...form, opticalLimitKes: Number(e.target.value) })
                  }
                >
                  {limitOptions(individualMedicalRates.optical.limits, true)}
                </select>
              </Field>
            </div>
          </div>
        </div>
      )}

      {wizard.currentStep.key === "review" && (
        <div className="fa-step-enter" key="review">
          <ReviewSummary
            sections={[
              {
                title: "Member Details",
                fields: [
                  { label: "ICPAK Member Number", value: identity.icpakMemberNumber },
                  { label: "First Name", value: identity.firstName },
                  { label: "Last Name", value: identity.lastName },
                  { label: "Phone", value: identity.phone },
                  { label: "Email", value: identity.email },
                ],
              },
              {
                title: "Family Information",
                fields: [
                  { label: "Age of Principal", value: String(form.ageOfPrincipal) },
                  { label: "Include Spouse", value: form.includeSpouse ? "Yes" : "No" },
                  { label: "Number of Children", value: String(form.numberOfChildren) },
                  { label: "Family Size", value: String(computeFamilySize({ includeSpouse: form.includeSpouse, numberOfChildren: form.numberOfChildren })) },
                  { label: "Cover Start Date", value: form.coverStartDate },
                ],
              },
              {
                title: "Coverage Limits",
                fields: [
                  { label: "Inpatient (Per Family)", value: `KES ${form.inpatientLimitKes.toLocaleString()}` },
                  { label: "Outpatient (Per Person)", value: `KES ${form.outpatientLimitKes.toLocaleString()}` },
                  { label: "Maternity (Per Family)", value: `KES ${form.maternityLimitKes.toLocaleString()}` },
                  { label: "Dental (Per Person)", value: `KES ${form.dentalLimitKes.toLocaleString()}` },
                  { label: "Optical (Per Person)", value: `KES ${form.opticalLimitKes.toLocaleString()}` },
                ],
              },
            ]}
          />
          {quote && (
            <>
              <RatesDisclaimer />
              <QuoteTable title="Individual/Family Medical Cover Premium" quote={quote} />
            </>
          )}
          <FormError message={submitError} />
        </div>
      )}

      <StepNavigation
        isFirstStep={wizard.isFirstStep}
        isLastStep={wizard.isLastStep}
        onBack={wizard.goBack}
        onNext={wizard.goNext}
        onSubmit={onSubmit}
        submitLoading={isSubmitting}
        submitDisabled={!isValid}
        submitLabel="Request for Detailed Quote"
      />
    </div>
  );
}
